import React, { useEffect, useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    MenuItem,
    Select,
    Collapse
} from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MuiDatePicker } from "../framework/muiDatepicker";
import { useTranslation } from "react-i18next";
import { PlannedWorkShiftSaveDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { FormikProps } from "formik";
import { Base } from "../../framework/base";
import { useParams } from "react-router-dom";
import { WorkShiftDialogParams } from "./WorkShiftPlanningRouter";
import {
    RecurringShift,
    RepeatEnabledDays16,
} from "../../models/recurringShift/recurringShift";

const weekDays = [
    { labelKey: "common.monday", value: RepeatEnabledDays16.Monday1 },
    { labelKey: "common.tuesday", value: RepeatEnabledDays16.Tuesday1 },
    { labelKey: "common.wednesday", value: RepeatEnabledDays16.Wednesday1 },
    { labelKey: "common.thursday", value: RepeatEnabledDays16.Thursday1 },
    { labelKey: "common.friday", value: RepeatEnabledDays16.Friday1 },
    { labelKey: "common.saturday", value: RepeatEnabledDays16.Saturday1 },
    { labelKey: "common.sunday", value: RepeatEnabledDays16.Sunday1 },
];

interface RecurrenceDialogProps {
    open: boolean;
    onClose: () => void;
    formik: FormikProps<PlannedWorkShiftSaveDto>;
    onSave?: () => void;
}

const INTERVAL_OPTIONS = [1, 2, 3, 4];

const RecurrenceDialog: React.FC<RecurrenceDialogProps> = ({
    open,
    onClose,
    formik,
    onSave
}) => {
    const { t } = useTranslation();
    const values = formik.values.recurrence || ({} as RecurringShift);
    const setFieldValue = formik.setFieldValue;
    const { date } = useParams<WorkShiftDialogParams>();

    const handleDayChange = (day: number) => {
        const current = values.repeatDays || 0;
        const newValue = current & day ? current & ~day : current | day;
        void setFieldValue("recurrence.repeatDays", newValue);
    };

    const handleCancel = () => {
        void setFieldValue("recurrence", formik.initialValues.recurrence);
        onClose();
    };

    const [showEndDate, setShowEndDate] = useState(!!values.recurrenceEndDate);

    const isDateRangeValid = useMemo(() => {
        if (!values.recurrenceStartDate) return true;
        if (!values.recurrenceEndDate) return true;
        return values.recurrenceStartDate < values.recurrenceEndDate;
    }, [values.recurrenceStartDate, values.recurrenceEndDate]);

    useEffect(() => {
        setShowEndDate(!!values.recurrenceEndDate);
    }, [open, values.recurrenceEndDate]);

    // Don't allow empty recurrenceStartDate
    useEffect(() => {
        if (open && !values.recurrenceStartDate) {
            void setFieldValue(
                "recurrence.recurrenceStartDate",
                // 1. Default to original recurrence start date
                // 2. Shift start date
                // 3. selected date
                // 4. today
                Base.dayjsToJsonDate(
                    formik.initialValues.recurrence?.recurrenceStartDate ??
                        formik.values.startTime ??
                        date ??
                        new Date()
                )
            );
        }
    }, [open, values.recurrenceStartDate]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {t("workShiftPlanning.recurring.recurrenceSettings")}
            </DialogTitle>
            <DialogContent>
                {/* Start Date */}
                <Box mb={2} mt={1}>
                    <MuiDatePicker
                        required
                        isClearable
                        label={t(
                            "workShiftPlanning.recurring.recurrenceStartDate"
                        )}
                        value={
                            values.recurrenceStartDate
                                ? new Date(values.recurrenceStartDate)
                                : null
                        }
                        onChange={(date) => {
                            const newStartDate = date ? Base.dayjsToJsonDate(date) : null;
                            void setFieldValue("recurrence.recurrenceStartDate", newStartDate);

                            if (newStartDate && values.recurrenceEndDate && newStartDate >= values.recurrenceEndDate) {
                                void setFieldValue("recurrence.recurrenceEndDate", null);
                                setShowEndDate(false);
                            }
                        }}
                        maxDate={values.recurrenceEndDate ? new Date(values.recurrenceEndDate) : undefined}
                        extraProps={{
                            textFieldProps: {
                                error: !isDateRangeValid,
                                helperText: !isDateRangeValid ? t("workShiftPlanning.recurring.startDateMustBeBeforeEnd") : undefined
                            }
                        }}
                    />
                </Box>

                {/* Interval */}
                <Box display="flex" alignItems="center" mb={2}>
                    <LoopIcon color="primary" sx={{ mr: 1 }} />
                    <Typography sx={{ mr: 2 }}>
                        {t("workShiftPlanning.recurring.recurrenceInterval")}
                    </Typography>
                    <Select
                        value={values.intervalInWeeks || 1}
                        onChange={(e) =>
                            void setFieldValue(
                                "recurrence.intervalInWeeks",
                                Number(e.target.value)
                            )
                        }
                        size="small"
                        sx={{ minWidth: 48, maxWidth: 60 }}
                    >
                        {INTERVAL_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography sx={{ ml: 1 }}>
                        {t("workShiftPlanning.recurring.weeks")}
                    </Typography>
                </Box>

                {/* Weekday Selection */}
                <Box mb={2}>
                    <Box display="flex" gap={1}>
                        {weekDays.map((day) => (
                            <IconButton
                                key={day.value}
                                onClick={() => handleDayChange(day.value)}
                                disableRipple
                                sx={{
                                    borderRadius: 0,
                                    bgcolor:
                                        values.repeatDays & day.value
                                            ? "primary.main"
                                            : "grey.200",
                                    color:
                                        values.repeatDays & day.value
                                            ? "primary.contrastText"
                                            : "text.secondary",
                                    border:
                                        values.repeatDays & day.value
                                            ? "2px solid"
                                            : "1px solid",
                                    borderColor:
                                        values.repeatDays & day.value
                                            ? "primary.dark"
                                            : "grey.300",
                                    width: 36,
                                    height: 36,
                                    fontSize: 18,
                                    fontWeight: "bold",
                                    boxShadow: "none",
                                }}
                            >
                                {t(day.labelKey).charAt(0).toUpperCase()}
                            </IconButton>
                        ))}
                    </Box>
                </Box>

                {/* End Date (foldable) */}
                <Box mb={2}>
                    <Button
                        variant="text"
                        size="small"
                        startIcon={
                            showEndDate ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )
                        }
                        onClick={() => {
                            if (showEndDate) {
                                setShowEndDate(false);
                                void setFieldValue(
                                    "recurrence.recurrenceEndDate",
                                    undefined
                                );
                            } else {
                                setShowEndDate(true);
                            }
                        }}
                        sx={{ mb: 1, textTransform: "none" }}
                    >
                        {showEndDate
                            ? t("workShiftPlanning.recurring.hideEndDate")
                            : t("workShiftPlanning.recurring.showEndDate")}
                    </Button>
                    <Collapse in={showEndDate}>
                        <MuiDatePicker
                            isClearable
                            label={t(
                                "workShiftPlanning.recurring.recurrenceEndDate"
                            )}
                            value={
                                values.recurrenceEndDate
                                    ? new Date(values.recurrenceEndDate)
                                    : null
                            }
                            onChange={(date) => {
                                if (date && values.recurrenceStartDate &&
                                    Base.dayjsToJsonDate(date) < values.recurrenceStartDate) {
                                    date = new Date(values.recurrenceStartDate);
                                }
                                void setFieldValue(
                                    "recurrence.recurrenceEndDate",
                                    date ? Base.dayjsToJsonDate(date) : null
                                );
                            }}
                            minDate={values.recurrenceStartDate ? new Date(values.recurrenceStartDate) : undefined}
                            extraProps={{
                                textFieldProps: {
                                    error: !isDateRangeValid,
                                    helperText: !isDateRangeValid ? t("workShiftPlanning.recurring.endDateMustBeAfterStart") : undefined
                                }
                            }}
                        />
                    </Collapse>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>{t("common.cancel")}</Button>
                <Button
                    onClick={() => {
                        if (onSave) {
                            onSave();
                        } else {
                            onClose();
                        }
                    }}
                    variant="contained"
                    disabled={!isDateRangeValid}
                >
                    {t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecurrenceDialog;